/**
 * Componente para configurar destino
 * VigosCloud - Message Context System
 * 
 * Reutilizável para configurar destinos de mensagem
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRoute, faInfoCircle, faClock } from '@fortawesome/free-solid-svg-icons';

import './MessageComponents.css';
import StasisFallbackInputs from '../../shared/StasisFallbackInputs';

function DestinationConfiguration({
  config = {
    destination_type: 'endpoint',
    destination: '',
    trunk_name: '',
    timeout_main: ''
  },
  onChange,
  availableEndpoints = [],
  availableQueues = [],
  availableContexts = [],
  availableTrunks = [],
  disabled = false,
  title = 'Configuração de Destino',
  showCycleOption = false,
  userRole = '',
  hasOverflows = false,
  errors = {},
  hasWhatsAppIntegration = false
}) {

  const handleChange = (field, value) => {
    if (!onChange) return;

    const next = {
      ...config,
      [field]: value
    };

    if (field === 'destination_type' && ['hangup', 'cycle', 'whatsapp'].includes(value)) {
      next.destination = '';
      next.trunk_name = '';
    }

    onChange(next);
  };

  const getAvailableDestinationTypes = () => {
    const types = [
      { value: 'endpoint', label: 'Ramal' },
      { value: 'queue', label: 'Fila' },
      { value: 'external', label: 'Externo' },
      { value: 'disa', label: 'DISA' },
      {
        value: 'whatsapp',
        label: hasWhatsAppIntegration ? 'WhatsApp' : 'WhatsApp (sem integração)',
        disabled: !hasWhatsAppIntegration
      },
      { value: 'hangup', label: 'Desligar' }
    ];

    if (showCycleOption) {
      types.push({ value: 'cycle', label: 'Ciclo (Reiniciar)' });
    }

    if (userRole === 'root') {
      types.push({ value: 'stasis', label: 'Agent IA' });
    }

    return types;
  };

  const renderDestinationInput = () => {
    switch (config.destination_type) {
      case 'endpoint':
        return (
          <input
            className="input"
            type="text"
            placeholder="Ex: 1000, 1001"
            value={config.destination || ''}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 4);
              handleChange('destination', value);
            }}
            maxLength={4}
            disabled={disabled}
          />
        );
      
      case 'queue':
        return (
          <div className="select is-fullwidth">
            <select
              value={config.destination || ''}
              onChange={(e) => handleChange('destination', e.target.value)}
              disabled={disabled}
            >
              <option value="">Selecione uma fila</option>
              {availableQueues.map((queue) => (
                <option key={queue.namequeue || queue.name} value={queue.namequeue || queue.name}>
                  {queue.namequeue || queue.name} - {queue.displayname || queue.namequeue || queue.name}
                </option>
              ))}
            </select>
          </div>
        );
      
      case 'external':
        return (
          <>
            <input
              className="input mb-2"
              type="text"
              placeholder="Ex: 1134567890"
              value={config.destination || ''}
              onChange={(e) => handleChange('destination', e.target.value)}
              disabled={disabled}
            />
            <div className="field mt-2">
              <label className="label is-small">Tronco *</label>
              <div className="select is-fullwidth">
                <select
                  value={config.trunk_name || ''}
                  onChange={(e) => handleChange('trunk_name', e.target.value)}
                  disabled={disabled}
                >
                  <option value="">Selecione o tronco</option>
                  {availableTrunks.map((trunk) => (
                    <option key={trunk.name} value={trunk.name}>
                      {trunk.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </>
        );
      
      case 'disa':
        return (
          <div className="select is-fullwidth">
            <select
              value={config.destination || ''}
              onChange={(e) => handleChange('destination', e.target.value)}
              disabled={disabled}
            >
              <option value="">Selecione um contexto</option>
              {availableContexts.filter(context => {
                const contextName = (context.name || context.context_name || '').toLowerCase();
                return contextName.includes('ura-') || contextName.includes('msg-');
              }).map((context) => (
                <option key={context.name || context.context_name} value={context.name || context.context_name}>
                  {context.label || context.name || context.context_name}
                </option>
              ))}
            </select>
          </div>
        );
      
      case 'whatsapp':
        return (
          <input
            className="input"
            type="text"
            placeholder="WhatsApp AGI"
            value="WhatsApp AGI"
            disabled
            style={{ backgroundColor: '#f5f5f5' }}
          />
        );
      
      case 'cycle':
        return (
          <input
            className="input"
            type="text"
            placeholder="Reiniciar fluxo"
            value="CYCLE_RESTART"
            disabled
            style={{ backgroundColor: '#f5f5f5' }}
          />
        );
      
      case 'hangup':
        return (
          <input
            className="input"
            type="text"
            placeholder="Desligar chamada"
            value="Desligar chamada"
            disabled
            style={{ backgroundColor: '#f5f5f5' }}
          />
        );

      case 'stasis':
        return (
          <>
            <input
              className="input mb-2"
              type="text"
              placeholder="App Stasis voice-gateway-ai"
              value="voice-gateway-ai (fixo)"
              disabled
              style={{ backgroundColor: '#f5f5f5' }}
            />
            <StasisFallbackInputs
              fallbackType={config.stasis_fallback_type || ''}
              fallbackDestination={config.stasis_fallback_destination || ''}
              onChange={(field, value) => handleChange(field, value)}
              availableEndpoints={availableEndpoints}
              availableQueues={availableQueues}
              availableContexts={availableContexts}
            />
          </>
        );

      default:
        return (
          <input
            className="input"
            type="text"
            placeholder="Destino"
            value={config.destination || ''}
            onChange={(e) => handleChange('destination', e.target.value)}
            disabled={disabled}
          />
        );
    }
  };

  const getDestinationHelp = () => {
    switch (config.destination_type) {
      case 'endpoint':
        return 'Digite um ramal de 4 dígitos numéricos (ex: 1000, 1001, 1234)';
      case 'queue':
        return 'Selecione a fila para onde a chamada será direcionada';
      case 'external':
        return 'Digite o número externo e selecione o tronco para a chamada';
      case 'disa':
        return 'Selecione o contexto DISA para acesso direto ao sistema';
      case 'whatsapp':
        return 'A chamada será encaminhada automaticamente para WhatsApp via AGI';
      case 'cycle':
        return 'Reinicia o fluxo desde o início (útil para loops)';
      case 'hangup':
        return 'A chamada será desligada automaticamente';
      case 'stasis':
        return 'Encaminha para o app Stasis voice-gateway-ai (IA). Configure o fallback abaixo';
      default:
        return '';
    }
  };

  return (
    <div className="form-section-card">
      <h3 className="title is-5 mb-4">
        <FontAwesomeIcon icon={faRoute} className="mr-2" />
        {title}
      </h3>

      <div className="field">
        <label className="label">Tipo de Destino Principal *</label>
        <div className="control">
          <div className="select is-fullwidth">
            <select
              value={config.destination_type || 'endpoint'}
              onChange={(e) => handleChange('destination_type', e.target.value)}
              disabled={disabled}
            >
              {getAvailableDestinationTypes().map(type => (
                <option 
                  key={type.value} 
                  value={type.value}
                  disabled={type.disabled}
                >
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p className="help">Selecione o tipo de destino para a mensagem</p>
      </div>

      {!['hangup', 'cycle', 'whatsapp'].includes(config.destination_type) && (
        <div className="field">
          <label className="label">Destino Principal *</label>
          <div className="control">
            {renderDestinationInput()}
          </div>
          <p className="help">
            <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
            {getDestinationHelp()}
          </p>
        </div>
      )}

      {['endpoint', 'queue', 'external'].includes(config.destination_type) && (
        <div className="field">
          <label className="label">
            <FontAwesomeIcon icon={faClock} className="mr-2" />
            Timeout (segundos) {config.destination_type !== 'queue' && '*'}
          </label>
          <div className="control">
            <input
              className={`input ${errors.timeout_main ? 'is-danger' : ''}`}
              type="number"
              min="1"
              max="300"
              placeholder={
                config.destination_type === 'queue'
                  ? (hasOverflows ? '15' : 'Sem timeout (indefinido)')
                  : config.destination_type === 'external'
                    ? (hasOverflows ? '60' : 'Sem timeout')
                    : (hasOverflows ? '20' : '60')
              }
              value={config.timeout_main ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') {
                  handleChange('timeout_main', '');
                } else {
                  const parsed = parseInt(raw, 10);
                  handleChange('timeout_main', Number.isNaN(parsed) ? '' : parsed);
                }
              }}
              disabled={disabled}
            />
          </div>
          {errors.timeout_main && <p className="help is-danger">{errors.timeout_main}</p>}
          {config.destination_type === 'endpoint' && (
            <p className="help">Tempo limite de toque para o ramal (1-300 segundos).</p>
          )}
          {config.destination_type === 'queue' && (
            <p className="help">
              Tempo máximo na fila antes do overflow.
              {hasOverflows ? ' Padrão: 15 segundos.' : ' Deixe vazio para aguardar indefinidamente.'}
            </p>
          )}
          {config.destination_type === 'external' && (
            <p className="help">Tempo limite para a chamada externa (1-300 segundos).</p>
          )}
        </div>
      )}
    </div>
  );
}

export default DestinationConfiguration;

